import React, { useEffect, useState } from 'react';
import { Spinner } from './base';

import { AUDIT_ICONS, AUDIT_ROLLBACK_TYPES } from '../lib/constantes';
import { db } from '../lib/firebase';

function AuditSidebar({ company, isAdmin, onRollback, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('');
  const [rolling, setRolling] = useState(null);
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    const unsub = db.collection('companies').doc(company).collection('audit_log')
      .orderBy('timestamp','desc').limit(200)
      .onSnapshot(snap => {
        setEntries(snap.docs.map(d => ({ id:d.id, ...d.data() })));
        setLoading(false);
      }, () => setLoading(false));
    return unsub;
  }, [company]);

  const fmtTime = ts => {
    if (!ts) return '';
    const d = new Date(ts), now = new Date(), diff = (now - d) / 1000;
    if (diff < 60)    return 'agora mesmo';
    if (diff < 3600)  return `${Math.floor(diff/60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h atrás`;
    return d.toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',year:'2-digit',hour:'2-digit',minute:'2-digit'});
  };

  const handleRollback = async (entry) => {
    if (!confirm(`Reverter: "${entry.description}"?\n\nEsta ação desfaz a operação registrada.`)) return;
    setRolling(entry.id);
    await onRollback(entry);
    setRolling(null);
  };

  const filtered = entries.filter(e => {
    if (filterType && e.type !== filterType) return false;
    if (filter) {
      const q = filter.toLowerCase();
      return (e.lead_name||'').toLowerCase().includes(q) ||
             (e.user_name||'').toLowerCase().includes(q) ||
             (e.user_email||'').toLowerCase().includes(q) ||
             (e.description||'').toLowerCase().includes(q);
    }
    return true;
  });

  // Group by date
  const grouped = [];
  let lastDate = null;
  filtered.forEach(e => {
    const dateStr = e.timestamp ? new Date(e.timestamp).toLocaleDateString('pt-BR',{weekday:'short',day:'2-digit',month:'2-digit'}) : '—';
    if (dateStr !== lastDate) { lastDate = dateStr; grouped.push({ isHeader:true, date:dateStr }); }
    grouped.push({ isHeader:false, entry:e });
  });

  const TYPE_LABELS = {
    stage_change:'Movimentação', lead_create:'Criação', lead_update:'Edição', lead_delete:'Exclusão',
    lead_import:'Importação CSV', interaction_add:'Interação +', interaction_delete:'Interação −', timeline_add:'Nota +', timeline_delete:'Nota −',
    deal_update:'Deal', rollback:'Reversão',
  };

  return <>
    <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
    <div className="animate-slide-right fixed top-0 right-0 h-full w-80 sm:w-96 z-50 flex flex-col shadow-2xl"
      style={{background:'rgba(249,249,251,0.97)',backdropFilter:'saturate(180%) blur(24px)',borderLeft:'0.5px solid rgba(0,0,0,0.1)'}}>

      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-black/[0.06]">
        <div>
          <h2 className="t-subhead font-semibold text-gray-900 tracking-tight">Histórico de Atividades</h2>
          <p className="t-caption-2 text-gray-400 mt-0.5">{filtered.length} registros</p>
        </div>
        <button aria-label="Fechar" onClick={onClose} className="tap-safe w-7 h-7 flex items-center justify-center rounded-full bg-black/[0.06] hover:bg-black/[0.1] text-gray-500 text-xs font-bold transition-colors">✕</button>
      </div>

      {/* Filters */}
      <div className="px-4 py-2.5 space-y-2 border-b border-black/[0.04]">
        <input className="apple-input w-full px-3 py-1.5 text-sm" placeholder="Buscar lead, usuário..." value={filter} onChange={e=>setFilter(e.target.value)} />
        <div className="flex gap-1 flex-wrap">
          {['','stage_change','lead_create','lead_update','lead_delete','rollback'].map(t => (
            <button key={t} onClick={()=>setFilterType(t)}
              className={`t-caption-2 px-2 py-0.5 rounded-full font-medium transition-all ${filterType===t ? 'bg-blue-500 text-white' : 'bg-black/[0.05] text-gray-500 hover:bg-black/[0.09]'}`}>
              {t ? (TYPE_LABELS[t]||t) : 'Todos'}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {loading
          ? <div className="flex justify-center py-10"><Spinner /></div>
          : filtered.length === 0
          ? <div className="text-center py-12"><div className="text-4xl mb-2">📭</div><p className="text-sm text-gray-400">Nenhuma atividade encontrada</p></div>
          : grouped.map((item, idx) => {
              if (item.isHeader) return (
                <div key={`h${idx}`} className="flex items-center gap-2 py-2 px-1 mt-2 first:mt-0">
                  <div className="flex-1 h-px bg-black/[0.06]" />
                  <span className="t-caption-2 font-bold text-gray-400 sf-caption whitespace-nowrap">{item.date}</span>
                  <div className="flex-1 h-px bg-black/[0.06]" />
                </div>
              );
              const e = item.entry;
              const canRollback = isAdmin && AUDIT_ROLLBACK_TYPES.includes(e.type);
              const typeLabel = TYPE_LABELS[e.type] || e.type;
              const isRollback = e.type === 'rollback';
              return (
                <div key={e.id} className={`rounded-xl p-3 mb-1.5 group border ${isRollback ? 'bg-orange-50/60 border-orange-100' : 'bg-white border-black/[0.06]'}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-sm flex-shrink-0 mt-0.5">{AUDIT_ICONS[e.type] || '📌'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`t-caption-2 font-bold sf-caption rounded-full px-1.5 py-0.5 ${
                          e.type==='lead_delete' ? 'bg-red-100 text-red-500' :
                          e.type==='lead_create' ? 'bg-green-100 text-green-600' :
                          e.type==='stage_change' ? 'bg-blue-100 text-blue-600' :
                          e.type==='rollback' ? 'bg-orange-100 text-orange-500' :
                          'bg-gray-100 text-gray-500'
                        }`}>{typeLabel}</span>
                      </div>
                      <p className="t-caption-1 text-gray-700 leading-snug">{e.description}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="t-caption-2 text-gray-400 truncate max-w-[120px]">{e.user_name || e.user_email}</span>
                        <span className="t-caption-2 text-gray-300">·</span>
                        <span className="t-caption-2 text-gray-400 whitespace-nowrap">{fmtTime(e.timestamp)}</span>
                      </div>
                    </div>
                    {canRollback && (
                      <button onClick={()=>handleRollback(e)} disabled={rolling===e.id}
                        className={`flex-shrink-0 flex items-center gap-1 t-caption-2 font-semibold px-2 py-1 rounded-full transition-all opacity-0 group-hover:opacity-100 whitespace-nowrap
                          ${rolling===e.id ? 'bg-orange-100 text-orange-400 opacity-100' : 'bg-orange-50 text-orange-500 hover:bg-orange-100'}`}
                        title="Reverter esta ação">
                        {rolling===e.id
                          ? <span className="w-2.5 h-2.5 border border-orange-400 border-t-transparent rounded-full animate-spin block" />
                          : '↩'}
                        Reverter
                      </button>
                    )}
                  </div>
                </div>
              );
            })
        }
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-black/[0.06] text-center">
        <p className="t-caption-2 text-gray-400">Somente admins podem reverter ações</p>
      </div>
    </div>
  </>;
}

// ─── DualScrollTable ──────────────────────────────────────

export { AuditSidebar };
